import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import ProgressRing from "@/components/ProgressRing";
import CheckoutModal from "@/components/CheckoutModal";
import { api, CATEGORIES } from "@/lib/config";
import { useTiers } from "@/lib/useTiers";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, Circle, Flame, Sparkles, Zap, Heart, Brain } from "lucide-react";
import { toast } from "sonner";

const categoryIcons = { energy: Zap, hormones: Flame, recovery: Heart, mindset: Brain };

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const { tiers } = useTiers();
  const navigate = useNavigate();
  const [protocol, setProtocol] = useState(null);
  const [progress, setProgress] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeTier, setUpgradeTier] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [p, pr, ls] = await Promise.all([
          api.get("/protocol/today"),
          api.get("/progress"),
          api.get("/lessons"),
        ]);
        setProtocol(p.data);
        setProgress(pr.data);
        setLessons(ls.data);
      } catch {}
    })();
  }, []);

  const toggleCheck = async (item) => {
    try {
      const next = !item.completed;
      const updated = protocol.items.map((it) =>
        it.key === item.key ? { ...it, completed: next } : it
      );
      setProtocol({
        ...protocol,
        items: updated,
        completed_count: updated.filter((i) => i.completed).length,
      });
      await api.post("/protocol/check", { item_key: item.key, completed: next });
      if (next && updated.every((i) => i.completed)) {
        toast.success("Full protocol done. Streak advanced. 🔥");
        refresh();
      }
    } catch {
      toast.error("Couldn't save — try again.");
    }
  };

  const nextTier = getNextTier(user?.tier);
  // Prefer a live tier for the user's next ladder rung; fall back to any active tier
  // if none matches (e.g., admin deactivated all core tiers).
  const upgradeObj = tiers.find((t) => t.grants_tier === nextTier)
                  || tiers.find((t) => (t.tier_key || t.key) === nextTier);

  const protocolPct = protocol ? Math.round((protocol.completed_count / protocol.total) * 100) : 0;

  const greeting = getGreeting();
  const firstName = user?.name?.split(" ")[0] || "Friend";

  const recentLessons = lessons
    .filter((l) => l.unlocked)
    .slice(0, 3);
  const categoryCounts = CATEGORIES.map((c) => ({
    ...c,
    count: lessons.filter((l) => l.category === c.key).length,
    unlocked: lessons.filter((l) => l.category === c.key && l.unlocked).length,
  }));

  return (
    <AppShell>
      <div className="space-y-8">
        {/* Greeting */}
        <div className="fade-up">
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#FF8A00]">{greeting}</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">Hey, {firstName}.</h1>
          <p className="text-white/50 mt-2">Your protocol for today, {formatDate()}.</p>
        </div>

        {/* Past due alert */}
        {protocol && !protocol.checked_in_today && (protocol.days_since_last_checkin ?? 0) >= 1 && (
          <div
            data-testid="past-due-alert"
            className={`fade-up glass rounded-2xl p-5 border flex flex-wrap items-center justify-between gap-3 ${
              protocol.days_since_last_checkin >= 3
                ? "border-red-500/30 bg-red-500/[0.04]"
                : "border-[#FF8A00]/25 bg-[#FF8A00]/[0.04]"
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                protocol.days_since_last_checkin >= 3
                  ? "bg-red-500/15 text-red-400"
                  : "bg-[#FF8A00]/15 text-[#FF8A00]"
              }`}>
                <Flame size={18} />
              </div>
              <div>
                <div className="text-sm text-white/85 font-medium">
                  {protocol.days_since_last_checkin >= 3
                    ? `You're ${protocol.days_since_last_checkin} days behind`
                    : `${protocol.days_since_last_checkin} day since your last check-in`}
                </div>
                <div className="text-xs text-white/50 mt-0.5">
                  {protocol.days_since_last_checkin >= 3
                    ? "Consistency compounds — let's get back on track today."
                    : "Log today's protocol to keep the streak going."}
                </div>
              </div>
            </div>
            <Button
              data-testid="past-due-cta"
              onClick={() => navigate("/protocol")}
              size="sm"
              className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]"
            >
              Log today <ArrowRight size={14} className="ml-1.5" />
            </Button>
          </div>
        )}

        {/* Top row: Ring + Streak + Upgrade */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          <div className="md:col-span-4 glass rounded-2xl p-6 flex flex-col items-center justify-center fade-up delay-1">
            <ProgressRing value={protocol?.completed_count || 0} max={protocol?.total || 8}
              size={180} stroke={14} label="Today's Protocol"
              sub={protocol ? `${protocol.completed_count} / ${protocol.total}` : "—"} />
          </div>

          <div className="md:col-span-4 glass rounded-2xl p-6 fade-up delay-2">
            <div className="text-[11px] tracking-[0.25em] uppercase text-white/40 mb-3">Streak</div>
            <div className="flex items-end gap-3">
              <div className="font-display text-6xl text-brand-gradient">{user?.streak || 0}</div>
              <div className="pb-2 text-sm text-white/60">day{user?.streak === 1 ? "" : "s"}</div>
            </div>
            <div className="flex items-center gap-2 mt-6 text-sm text-white/60">
              <Flame size={16} className="text-[#FF8A00]" />
              Complete every daily protocol item to extend your streak.
            </div>
          </div>

          <div className="md:col-span-4 glass rounded-2xl p-6 relative overflow-hidden fade-up delay-3">
            {upgradeObj ? (
              <>
                <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#FF8A00]/20 blur-3xl" />
                <div className="text-[11px] tracking-[0.25em] uppercase text-[#FF8A00] mb-3 relative z-10">Next step</div>
                <h3 className="font-display text-2xl leading-tight relative z-10">{upgradeObj.name}</h3>
                <p className="text-sm text-white/55 mt-2 relative z-10">{upgradeObj.sub}</p>
                <div className="mt-4 flex items-baseline gap-2 relative z-10">
                  <div className="font-display text-3xl">{upgradeObj.price}</div>
                  <div className="text-xs text-white/40">{upgradeObj.cadence}</div>
                </div>
                <Button
                  data-testid="dashboard-upgrade-btn"
                  onClick={() => { setUpgradeTier(upgradeObj.tier_key || upgradeObj.key); setUpgradeOpen(true); }}
                  className="mt-5 w-full rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71] relative z-10"
                >
                  Unlock now <ArrowRight className="ml-2" size={14} />
                </Button>
              </>
            ) : (
              <>
                <div className="text-[11px] tracking-[0.25em] uppercase text-[#FF8A00] mb-3">Top tier</div>
                <h3 className="font-display text-2xl">You've unlocked it all.</h3>
                <p className="text-sm text-white/55 mt-2">
                  You're in the Cohort. See you in the next live session.
                </p>
                <Sparkles className="text-[#FF8A00] mt-4" />
              </>
            )}
          </div>
        </div>

        {/* Daily checklist */}
        <div className="glass rounded-2xl p-6 fade-up delay-2">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-[11px] tracking-[0.25em] uppercase text-[#FF8A00]">Your Protocol Today</div>
              <h2 className="font-display text-3xl mt-1">{protocolPct}% complete</h2>
            </div>
            <Link to="/protocol" className="text-xs text-white/50 hover:text-white" data-testid="view-full-protocol">
              View full
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {protocol?.items?.map((item) => {
              const Icon = categoryIcons[item.category] || Circle;
              return (
                <button
                  key={item.key}
                  onClick={() => toggleCheck(item)}
                  data-testid={`protocol-item-${item.key}`}
                  className={`text-left flex items-start gap-3 rounded-xl p-4 border transition-all ${
                    item.completed
                      ? "bg-[#FF8A00]/8 border-[#FF8A00]/25"
                      : "bg-[#0A0A0A] border-white/5 hover:border-white/15"
                  }`}
                >
                  <div className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                    item.completed
                      ? "bg-gradient-to-br from-[#FF8A00] to-[#E52E71] check-pop"
                      : "border border-white/20"
                  }`}>
                    {item.completed && <CheckCircle2 size={14} />}
                  </div>
                  <div className="flex-1">
                    <div className={`text-sm ${item.completed ? "text-white" : "text-white/85"}`}>{item.label}</div>
                    <div className="text-[11px] text-white/45 mt-1">{item.description}</div>
                  </div>
                  <Icon size={14} className="text-white/30 mt-1" />
                </button>
              );
            })}
          </div>
        </div>

        {/* Continue learning + categories */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div className="lg:col-span-8 fade-up delay-3">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl">Continue Learning</h2>
              <Link to="/learn" className="text-xs text-white/50 hover:text-white" data-testid="view-all-lessons">View all</Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {recentLessons.map((l) => (
                <Link
                  key={l.lesson_id}
                  to={`/learn/${l.lesson_id}`}
                  data-testid={`lesson-quick-${l.lesson_id}`}
                  className="group rounded-xl overflow-hidden bg-[#0A0A0A] border border-white/5 hover:border-[#FF8A00]/30 transition-all"
                >
                  <div className="aspect-video bg-gradient-to-br from-[#FF8A00]/20 to-[#E52E71]/10 flex items-center justify-center relative">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(255,138,0,0.3),transparent_60%)]" />
                    <div className="font-display text-3xl text-white/30">{l.order}</div>
                  </div>
                  <div className="p-4">
                    <div className="text-[10px] tracking-[0.22em] uppercase text-[#FF8A00]">{l.category}</div>
                    <div className="mt-2 text-sm">{l.title}</div>
                  </div>
                </Link>
              ))}
              {recentLessons.length === 0 && (
                <div className="col-span-3 text-white/40 text-sm">Unlock your first tier to start learning.</div>
              )}
            </div>
          </div>

          <div className="lg:col-span-4 fade-up delay-4">
            <h2 className="font-display text-2xl mb-4">Library</h2>
            <div className="space-y-2">
              {categoryCounts.map((c) => (
                <Link
                  key={c.key}
                  to="/learn"
                  className="flex items-center justify-between p-4 rounded-xl bg-[#0A0A0A] border border-white/5 hover:border-[#FF8A00]/30"
                  data-testid={`cat-${c.key}`}
                >
                  <div>
                    <div className="text-sm">{c.label}</div>
                    <div className="text-[11px] text-white/40">{c.unlocked}/{c.count} unlocked</div>
                  </div>
                  <div className="text-xs" style={{ color: c.accent }}>
                    {Math.round((c.unlocked / Math.max(c.count, 1)) * 100)}%
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      <CheckoutModal open={upgradeOpen} onOpenChange={setUpgradeOpen} tierKey={upgradeTier} />
    </AppShell>
  );
}

function getNextTier(cur) {
  const order = ["free", "workshop", "course", "membership", "cohort"];
  const idx = order.indexOf(cur || "free");
  if (idx < 0 || idx >= order.length - 1) return null;
  return order[idx + 1];
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 5) return "Still here";
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function formatDate() {
  return new Date().toLocaleDateString(undefined, {
    weekday: "long", month: "long", day: "numeric"
  });
}
